import { Planogram } from 'app/types/machines/planogram'
import { MachineRelation } from 'app/types/park/machineRelation'
import {
    ProductStockReport,
    TerminalStockReport,
} from 'app/types/reports/stock/final'

type ProductBbd = ProductStockReport & {
    date: string
}

/** [MOCKS] Get the product formatted for the stock handler with the given machine. */
const getProductsFormattedMachine = (
    planogram: Planogram,
): ProductStockReport[] => {
    const productsBbd: ProductBbd[] = []

    // Get all the BBDs
    for (const selection of planogram.selections) {
        for (const bbd of selection.articles) {
            if ('product' in selection) {
                productsBbd.push({
                    id: selection.product.id,
                    name: selection.product.name,
                    ean: selection.product.ean,
                    date: bbd.bbd,
                    quantity: 1,
                })
            }
        }
    }

    // Group by products
    const productsFormatted: ProductStockReport[] = []

    for (const productBbd of productsBbd) {
        const productFound = productsFormatted.find(
            (product) => product.id === productBbd.id,
        )

        // Product not found
        if (!productFound) {
            productsFormatted.push({
                id: productBbd.id,
                name: productBbd.name,
                ean: productBbd.ean,
                bbds: [
                    {
                        date: productBbd.date,
                        quantity: productBbd.quantity,
                    },
                ],
            })
        }

        // Product found
        if (productFound) {
            const bbdFound = productFound.bbds.find(
                (bbd) => bbd.date === productBbd.date,
            )

            if (!bbdFound) {
                productFound.bbds.push({
                    date: productBbd.date,
                    quantity: productBbd.quantity,
                })
            }

            if (bbdFound) {
                bbdFound.quantity += productBbd.quantity
            }
        }
    }

    return productsFormatted
}

/** [MOCKS] Get the product formatted for the stock handler with the given machine. */
export const getProductsFormattedMachineRelation = (
    machineRelation: MachineRelation,
): TerminalStockReport => {
    const terminalFormatted: TerminalStockReport = {
        id: machineRelation.machine.id,
        name: machineRelation.machine.name,
        products: [],
    }

    // Distributors
    for (const distributor of machineRelation.distributors) {
        const products = getProductsFormattedMachine(distributor)

        for (const product of products) {
            const productFound = terminalFormatted.products.find(
                (productToCompare) => productToCompare.id === product.id,
            )

            // Product found
            if (productFound) {
                for (const bbd of product.bbds) {
                    const bbdFound = productFound.bbds.find(
                        (bbdToSearch) => bbdToSearch.date === bbd.date,
                    )

                    // BBD found
                    if (bbdFound) {
                        bbdFound.quantity += bbd.quantity
                    }

                    // BBD not found
                    if (!bbdFound) {
                        productFound.bbds.push(bbd)
                    }
                }
            }

            // Product not found
            if (!productFound) {
                terminalFormatted.products.push(product)
            }
        }
    }

    // Lockers
    for (const locker of machineRelation.lockers) {
        const products = getProductsFormattedMachine(locker)

        for (const product of products) {
            const productFound = terminalFormatted.products.find(
                (productToCompare) => productToCompare.id === product.id,
            )

            // Product found
            if (productFound) {
                for (const bbd of product.bbds) {
                    const bbdFound = productFound.bbds.find(
                        (bbdToSearch) => bbdToSearch.date === bbd.date,
                    )

                    // BBD found
                    if (bbdFound) {
                        bbdFound.quantity += bbd.quantity
                    }

                    // BBD not found
                    if (!bbdFound) {
                        productFound.bbds.push(bbd)
                    }
                }
            }

            // Product not found
            if (!productFound) {
                terminalFormatted.products.push(product)
            }
        }
    }

    // Fridge weight
    for (const fridgeWeight of machineRelation.weighted_fridges) {
        const products = getProductsFormattedMachine(fridgeWeight)

        for (const product of products) {
            const productFound = terminalFormatted.products.find(
                (productToCompare) => productToCompare.id === product.id,
            )

            // Product found
            if (productFound) {
                for (const bbd of product.bbds) {
                    const bbdFound = productFound.bbds.find(
                        (bbdToSearch) => bbdToSearch.date === bbd.date,
                    )

                    // BBD found
                    if (bbdFound) {
                        bbdFound.quantity += bbd.quantity
                    }

                    // BBD not found
                    if (!bbdFound) {
                        productFound.bbds.push(bbd)
                    }
                }
            }

            // Product not found
            if (!productFound) {
                terminalFormatted.products.push(product)
            }
        }
    }

    return terminalFormatted
}
